import { Stack } from '@chakra-ui/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { H3, H5, H6 } from '@/components/Headings';
import { colors } from '@/styles/theme/colors';
import { TextSmall } from '@/components/Texts';

const AmlKyc = () => {
  const router = useRouter();

  return (
    <Stack w='full' maxW={'8xl'}>
      <Stack
        w='full'
        textAlign={'start'}
        spacing={2}
        py={{ base: 4, md: 12 }}
        maxW={'100%'}
      >
        <Stack w={'full'} textAlign={'left'}>
          <H3>Anti-Money Laundering & Know Your Customer Policies</H3>
        </Stack>
        <TextSmall>
          By agreeing to the General Terms and Conditions located{' '}
          <TextSmall
            cursor={'pointer'}
            as={'span'}
            color={'blue'}
            textDecoration={'underline'}
            onClick={() => {
              router.push(`?tab=`, undefined, {
                shallow: true,
                scroll: true,
              });
            }}
          >
            here
          </TextSmall>
          , Members also acknowledge that the Company has implemented the
          AML/KYC program as described in this disclosure. All capitalized terms
          not defined herein shall have the meaning ascribed to them in the
          General Terms, as applicable.
        </TextSmall>

        <H5 mt={8} fontWeight={700}>
          <H5 fontWeight={500} as={'span'} color={colors.diment.lightBlue}>
            1)
          </H5>{' '}
          Introduction
        </H5>
        <TextSmall>
          DIMENT has to establish group-wide applicable policies and procedures,
          including data protection policies as well as policies and procedures
          for the exchange of information within the group for the purposes of
          combating money laundering and the money laundering and terrorist
          financing. These policies and procedures must be effectively
          implemented.
        </TextSmall>
        <TextSmall>
          For this purpose the present policy defines minimum standards on the
          basis of EU Directive (EU) 2015/849. If local regulations are stricter
          than the minimum standards defined in this policy, the stricter
          regulations shall be applied. If there is a conflict between relevant
          laws and this Policy, the company or individual concerned shall inform
          the Compliance unit and the Group Money Laundering Officer in order to
          resolve the conflict.
        </TextSmall>
        <TextSmall>
          DIMENT is committed to the highest standards in the prevention of
          money laundering and terrorist financing and requires all its
          employees and managers to comply with these standards in order to
          prevent its products and services from being misused for money
          laundering and terrorist financing.{' '}
        </TextSmall>
        <TextSmall>
          Diment makes use of the service of sumsub{' '}
          <Link href={'https://sumsub.com/'} target='_blank'>
            (https://sumsub.com/)
          </Link>
          . You will find further information, which is not part of this policy,
          on their website. The internal measures described here provide a
          crucial insight into our KYC/AML procedure; however, they are not
          exhaustive.
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          <H5 fontWeight={500} as={'span'} color={colors.diment.lightBlue}>
            2)
          </H5>{' '}
          Internal measures
        </H5>
        <TextSmall>
          The Company protects itself from involvement in money laundering or
          suspicious activity by the following:
        </TextSmall>
        <TextSmall whiteSpace={'pre-line'} pl={4}>
          {`• A system of internal controls designed to assure ongoing AML compliance;
      • Independent testing of AML compliance through an annual, independent AML audit;
      • Designation of a Compliance Officer for managing AML Compliance; and
      • AML training for all employees.
      `}
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          INTERNAL CONTROLS
        </H5>
        <TextSmall>
          The Company has established a set of AML/KYC policies and procedures
          which are approved by the Company’s Board. The approved policies will
          be provided to all employees. All policies and procedures will be
          reviewed and updated or revised as needed, but no less often than
          annually.
        </TextSmall>
        <TextSmall>
          The Company has developed and implemented internal controls for the
          purpose of ensuring that all of its operations comply with AML
          requirements and that all required reports are made on a timely basis.
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          INDEPENDENT TESTING
        </H5>
        <TextSmall>
          The Company’s AML program will be subject to independent testing
          through an annual, independent AML audit. The audit will be conducted
          by an independent third party with working knowledge of AML
          requirements, or by Company personnel with working knowledge of AML
          requirements, none of whom work for or with the Compliance Officer.
          The Compliance Officer will develop corrective action plans for all
          issues that are raised in the audit, supervise the remediation
          performed, and report all updates to the corrective action plans to
          the Company’s senior management.
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          COMPLIANCE OFFICER
        </H5>
        <TextSmall>
          The Company has appointed a Compliance Officer to be responsible for
          the management, coordination and monitoring of compliance with this
          policy and all applicable AML laws and regulations. The Compliance
          Officer will have working knowledge of all AML laws and be qualified
          by knowledge, experience and training. The Compliance Officer will be
          responsible for filing (if applicable) and keeping a record of
          suspicious transaction reports (“STR”) and suspicious activity reports
          (“SAR”). The Compliance Officer will oversee all corrective action of
          any audit findings or other AML-related issues from the annual,
          independent AML audit. The Compliance Officer will be responsible for
          all record keeping requirements and provide reports on the
          effectiveness of the AML program to the Company’s Board.
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          TRAINING
        </H5>
        <TextSmall>
          All of the officers and employees of the Company are required to
          receive AML training at least annually. The Company will track the
          training progress of all employees and maintain documentation of each
          employee, the date of the AML training as well as a description of
          such training. New employees will receive appropriate AML training
          within 30 days of their hire date. Training for all employees will
          include not only the legal elements of AML laws and regulations but
          will also cover job specific applications of these laws. Ongoing
          training will be provided and updated regularly to reflect current
          developments and changes to laws and regulations.
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          CUSTOMER IDENTIFICATION PROGRAM
        </H5>
        <TextSmall>
          Company has developed and implemented a Customer Identification
          Program (“CIP”) that establishes procedures for verifying the identity
          of each customer that opens a new account on the Company’s platform.
          It is the Company’s policy to ensure that it has reasonably identified
          each customer who uses the Company’s platform.
        </TextSmall>
        <TextSmall>
          As a result of the risk analysis on company level according to Art 8
          of the Directive (EU) 2015/849 DIMENT is of the opinion that the risk
          of being misused for the purpose of money laundering and financing
          terrorism financing is directly related to the customers - the more
          plausible and comprehensible the information about customers, the
          lower the risk in this context.
        </TextSmall>
        <TextSmall>
          This residual risk can be reduced even further if the risk in relation
          to customer groups and transaction behaviour is already defined in
          advance. This will enable DIMENT to develop measures and controls in
          order to keep this residual risk as low as possible.
        </TextSmall>
        <TextSmall>
          For the purpose of preventing money laundering and financing of
          terrorism, DIMENT does not maintain business relations with the
          following customer groups:
        </TextSmall>
        <TextSmall textDecoration={'underline'}>
          Characteristics of customers/companies
        </TextSmall>
        <TextSmall whiteSpace={'pre-line'} pl={4}>
          {`• Persons who are on sanction lists;
    • persons about whom information concerning a possible involvement in criminal activities is available activities exist or are indicated;
    • clients with country of registration/permanent residence in an offshore destination, in a non-cooperative country, in a non-recognised country or in a third country which has not yet had the opportunity to gain experience in cooperating;
    • companies with anonymous bearer securities or bearer shares;
    • Corporate structures with off-shore relationships, trusts or other forms (e.g. nominee shareholder) that do not clearly identify the beneficial owner, unless, the structure is disclosed up to the beneficial owner;
    • Non-profit associations, taking into account the organisational purpose, the country of origin or registration and the activity with frequent payments abroad. 
      `}
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          ACCOUNT OPENING PROCEDURES
        </H5>
        <TextSmall>
          Additionally, the Company will, as part of its account opening
          process: (i) cross-check the names of users against compliance
          databases such as the OFAC Specially Designated Nationals list and
          other governmental watch lists; (ii) require users to verify and
          validate their identity and identification documents presented at
          onboarding; and (iii) not permit any activity on platform with
          incomplete account opening information.
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          IDENTITY VERIFICATION
        </H5>
        <H6 fontWeight={500}>Individual</H6>
        <TextSmall whiteSpace={'pre-line'} pl={4}>
          {`• General personal date (Full name, sex, personal identification code or number, date of birth, legal capacity, nationality and citizenship, location (street, city, country, and postcode).
    • Banking Details (Cardholder name, expiry date, first 6 and last 4 digits of the card number)
    • Contact Details (Address, e-mail address, and phone number)
    • Biometrical Data
    • Facial Image Data
    • Identification number (e.g., social security number)
    • Acceptable and valid government-issued identification document (e.g., drivers license, passport, national identification card)`}
        </TextSmall>
        <H6 mt={4} fontWeight={500}>
          Institutions
        </H6>
        <TextSmall whiteSpace={'pre-line'} pl={4}>
          {`•   	Institution name
      •   	The address of the institution’s principal place of business and, if different, the institution’s mailing/registered address
      •   	Identification number of the institution (e.g., employer identification number)
      •   	Name of institution’s representative/user for the account
      •   	For institutions, the Company will collect the identifying information with respect to each beneficial owner and will use risk-based procedures to verify the identity of such beneficial owners, including:
      •   Acceptable and valid government-issued identification document (e.g., drivers license, passport, national identification card) for each beneficial owner; and
      •   Proof of residency (e.g., utility bills, government issued correspondence, documentation issued by recognized financial institutions).
      •   	Customer due diligence procedures will include additional information regarding the institution including but not limited to the following:
      •   A description of the institution’s business, including products and services, main customer types, and geographies served;
      •   Purpose of account; and
      •   Source of funds.`}
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          SUSPICIOUS TRANSACTION AND ACTIVITY REPORTING
        </H5>
        <TextSmall>
          The Company maintains a transaction monitoring program reasonably
          designed for the purpose of monitoring transactions for potential AML
          violations and suspicious activity reporting. Transactions that are
          unusual will be carefully reviewed to determine if it appears to be
          involved with money laundering, tax evasion, terrorist financing, or
          other illegal or criminal activity.
        </TextSmall>
        <TextSmall>
          Upon identification of potential suspicious transactions or activity,
          the Compliance Officer will then consult with the Company’s Chief
          Compliance Officer to determine whether to perform a filing for
          suspicious activity. The Compliance Officer will document any
          suspicious activity, the determinations made with regard to the
          activity and the determination as to whether a report of suspicious
          activity is required pursuant to applicable law or regulation. A
          filing and any information that would reveal the existence of a
          filing, are confidential.
        </TextSmall>
        <H5 mt={8} fontWeight={700}>
          <H5 fontWeight={500} as={'span'} color={colors.diment.lightBlue}>
            3)
          </H5>{' '}
          Third party sumsub
        </H5>
        <TextSmall>
          Diment uses the service of sumsub. You may be bound by the terms and
          conditions{' '}
          <Link
            style={{ color: 'blue', textDecoration: 'underline' }}
            href={'https://sumsub.com/terms-of-use/'}
            target='_blank'
          >
            (https://sumsub.com/terms-of-use/)
          </Link>{' '}
          <Link
            style={{ color: 'blue', textDecoration: 'underline' }}
            href={'https://sumsub.com/privacy-notice-service/'}
            target='_blank'
          >
            (https://sumsub.com/privacy-notice-service/)
          </Link>{' '}
          of these third party, and Diment may not be held responsible for any
          losses that may result from the actions of these third parties.
        </TextSmall>
        <Stack mt={4} w='full'>
          <TextSmall textAlign={'end'} fontWeight={700}>
            7.12.2023
          </TextSmall>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AmlKyc;
